'use client';

import { useQuery } from '@apollo/client/react';

import { GetFeatureDiscoveryStatesDocument } from './feature-discovery.graphql';

/**
 * Hook to determine if a "new feature" indicator should be shown for a given feature key.
 *
 * Returns `true` if the feature has NOT been DISMISSED or ACTED upon.
 * Returns `false` if loading, no feature key provided, or feature was dismissed/acted.
 *
 * @param featureKey - The feature discovery key to check (e.g., 'CAMPAIGNS')
 * @returns boolean indicating whether to show the new feature indicator
 *
 * @example
 * const showIndicator = useShowNewFeatureIndicator('CAMPAIGNS');
 * if (showIndicator) {
 *   // Render the red dot indicator
 * }
 */
export function useShowNewFeatureIndicator(featureKey: string | undefined): boolean {
  const { data, loading } = useQuery(GetFeatureDiscoveryStatesDocument, {
    variables: { input: { keys: featureKey ? [featureKey] : [] } },
    skip: !featureKey,
  });

  if (!featureKey || loading || !data) {
    return false;
  }

  const featureState = data.getFeatureDiscoveryStates?.find((state) => state.key === featureKey)?.status;

  // Show indicator if status is NOT 'DISMISSED' and NOT 'ACTED'
  // This includes 'UNSEEN', 'SEEN', or undefined (new feature never queried before)
  return featureState !== 'DISMISSED' && featureState !== 'ACTED';
}
